import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  Users, DollarSign, Calendar as CalendarIcon, Video,
  MapPin, Phone, Mail, MoreHorizontal, Clock, CalendarX, Compass, Sparkles
} from 'lucide-react';
import api from '../lib/api';
import { SetupProgressCard } from '../components/SetupProgressCard';
import useAuthStore from '../lib/auth-store';

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function getCurrentWeek() {
  const today = new Date();
  const jsDay = today.getDay(); // 0 = Sun
  const mondayOffset = jsDay === 0 ? -6 : 1 - jsDay;
  const monday = new Date(today);
  monday.setDate(today.getDate() + mondayOffset);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [todaysSchedule, setTodaysSchedule] = useState([]);
  const [salesGps, setSalesGps] = useState(null);
  const [loading, setLoading] = useState(true);
  const isRep = user?.role === 'iso_agent' || user?.role === 'iso_subagent';

  useEffect(() => {
    loadDashboardData();
    if (isRep) {
      api.getSalesGPSDashboard().then(setSalesGps).catch(() => {});
    }
  }, [isRep]);

  const loadDashboardData = async () => {
    try {
      const [dashboardStats, leadsData, activityData, myDay] = await Promise.all([
        api.getDashboardStats(),
        api.getLeads(),
        api.getRecentActivity(6).catch(() => []),
        api.getMyDay().catch(() => ({}))
      ]);

      const followUpsToday = leadsData.filter(l => l.status === 'contacted').length;
      const demosScheduled = dashboardStats.applications?.in_progress || 0;

      setStats({
        totalLeads: dashboardStats.leads?.total || 0,
        pipelineValue: dashboardStats.pipeline?.value || 0,
        openDeals: dashboardStats.pipeline?.open_deals || 0,
        followUpsToday,
        demosScheduled,
        leadsBreakdown: {
          new: dashboardStats.leads?.new || 0,
          contacted: dashboardStats.leads?.contacted || 0,
          qualified: dashboardStats.leads?.qualified || 0
        }
      });

      setRecentLeads(leadsData.slice(0, 5));
      setRecentActivity(activityData || []);

      const combined = [
        ...(myDay.due_today || []),
        ...(myDay.calls || []),
        ...(myDay.meetings || [])
      ];
      const seen = new Set();
      const todays = [];
      combined.forEach(t => {
        if (t.id && !seen.has(t.id)) {
          seen.add(t.id);
          todays.push(t);
        }
      });
      todays.sort((a, b) => new Date(a.due_date || 0) - new Date(b.due_date || 0));
      setTodaysSchedule(todays.slice(0, 5));
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      hot: 'bg-orange-100 text-orange-700 border-orange-200',
      warm: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      cold: 'bg-blue-100 text-blue-700 border-blue-200',
      new: 'bg-green-100 text-green-700 border-green-200'
    };
    return colors[status?.toLowerCase()] || colors.new;
  };

  const isCallOrMeeting = (taskType) => ['call', 'follow_up_call'].includes(taskType) ? 'phone' : 'video';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const week = getCurrentWeek();
  const todayStr = new Date().toDateString();

  return (
    <div className="space-y-6" data-testid="dashboard-page">
        <SetupProgressCard />
        {isRep && (
          <Card className="p-5" data-testid="dashboard-sales-gps-widget">
            {!salesGps ? (
              <div className="text-sm text-muted-foreground">Loading Sales GPS...</div>
            ) : !salesGps.goal_set ? (
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Compass className="h-8 w-8 text-primary flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Activate your Sales GPS</h3>
                    <p className="text-sm text-muted-foreground">Set a monthly goal and we'll tell you exactly what to do today to hit it.</p>
                  </div>
                </div>
                <Button size="sm" onClick={() => navigate('/sales-gps')} data-testid="dashboard-sales-gps-activate-btn">Set Goal</Button>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-6 flex-wrap">
                <div className="flex items-center gap-3 min-w-[200px]">
                  <Compass className="h-8 w-8 text-primary flex-shrink-0" />
                  <div>
                    <div className="text-2xl font-bold" data-testid="dashboard-sales-gps-boarded">
                      {salesGps.goal_progress.boarded_mtd}<span className="text-muted-foreground text-base">/{salesGps.monthly_boarded_goal} boarded</span>
                    </div>
                    <Progress value={salesGps.goal_progress.pct_complete} className="h-1.5 w-40 mt-1" />
                  </div>
                </div>
                {salesGps.coaching ? (
                  <div className="flex items-start gap-2 flex-1 min-w-[240px] bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <Sparkles className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-900 line-clamp-2" data-testid="dashboard-sales-gps-coaching">{salesGps.coaching.message}</p>
                  </div>
                ) : (
                  <p className="text-sm text-emerald-700 flex-1">You're on track — no bottleneck flagged today.</p>
                )}
                <Button variant="ghost" size="sm" onClick={() => navigate('/sales-gps')} data-testid="dashboard-sales-gps-view-btn">View Full Sales GPS</Button>
              </div>
            )}
          </Card>
        )}
        {/* Top Metrics Row */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {/* Total Leads */}
          <Card className="p-6" data-testid="dashboard-total-leads-card">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Total Leads</p>
                <h3 className="text-3xl font-bold">{stats?.totalLeads || 0}</h3>
                <p className="text-xs text-muted-foreground">across your pipeline</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </Card>

          {/* Pipeline Value */}
          <Card className="p-6" data-testid="dashboard-pipeline-value-card">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Pipeline Value</p>
                <h3 className="text-3xl font-bold">${(stats?.pipelineValue || 0).toLocaleString()}</h3>
                <p className="text-xs text-muted-foreground">{stats?.openDeals || 0} open deal{stats?.openDeals === 1 ? '' : 's'}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </Card>

          {/* Follow-Ups Today */}
          <Card className="p-6" data-testid="dashboard-followups-card">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Follow-Ups Today</p>
                <h3 className="text-3xl font-bold">{stats?.followUpsToday || 0}</h3>
                <p className="text-xs text-muted-foreground">leads in contacted status</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <CalendarIcon className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </Card>

          {/* Demos / Applications In Progress */}
          <Card className="p-6" data-testid="dashboard-demos-card">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Applications In Progress</p>
                <h3 className="text-3xl font-bold">{stats?.demosScheduled || 0}</h3>
                <p className="text-xs text-muted-foreground">being worked right now</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Video className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Middle Row - 3 Columns */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Lead Pipeline */}
          <Card className="p-6 lg:col-span-1">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Lead Pipeline</h3>
              <Button variant="ghost" size="sm" onClick={() => navigate('/leads')}>View All</Button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="font-medium">New</span>
                </div>
                <span className="text-xl font-bold">{stats?.leadsBreakdown?.new || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="font-medium">Contacted</span>
                </div>
                <span className="text-xl font-bold">{stats?.leadsBreakdown?.contacted || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="font-medium">Qualified</span>
                </div>
                <span className="text-xl font-bold">{stats?.leadsBreakdown?.qualified || 0}</span>
              </div>
            </div>
          </Card>

          {/* Today's Schedule */}
          <Card className="p-6 lg:col-span-2" data-testid="dashboard-todays-schedule">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Today's Schedule</h3>
              <Button variant="ghost" size="sm" onClick={() => navigate('/tasks')}>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </div>
            {todaysSchedule.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center text-muted-foreground" data-testid="dashboard-schedule-empty">
                <CalendarX className="h-8 w-8 mb-2 opacity-50" />
                <p className="text-sm">Nothing scheduled today — plan your day from Tasks</p>
              </div>
            ) : (
              <div className="space-y-3">
                {todaysSchedule.map((task) => {
                  const due = task.due_date ? new Date(task.due_date) : null;
                  return (
                    <div key={task.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent transition-colors" data-testid={`schedule-item-${task.id}`}>
                      <div className="flex items-center space-x-4">
                        <div className="text-center min-w-[64px]">
                          <div className="text-sm font-semibold">
                            {due ? due.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : 'No time'}
                          </div>
                        </div>
                        <div className="h-10 w-px bg-border"></div>
                        <div>
                          <div className="font-medium text-sm">{task.title}</div>
                          <div className="text-xs text-muted-foreground flex items-center space-x-1">
                            <Clock className="h-3 w-3" />
                            <span>{task.subject_name || task.task_type}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          {isCallOrMeeting(task.task_type) === 'phone' ? <Phone className="h-4 w-4" /> : <Video className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>

        {/* Bottom Row - 2 Columns */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Recent Leads Table - Takes 2 columns */}
          <Card className="p-6 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">Recent Leads</h3>
              <Button variant="ghost" size="sm" onClick={() => navigate('/leads')}>
                View All
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left text-sm text-muted-foreground">
                    <th className="pb-3 font-medium">Name/Business</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium">Date Added</th>
                    <th className="pb-3 font-medium">Owner</th>
                    <th className="pb-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentLeads.map((lead) => {
                    const initials = lead.contact_name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'NA';
                    const ownerInitials = lead.owner_name?.split(' ').map(n => n[0]).join('').toUpperCase() || '—';
                    return (
                      <tr key={lead.id} className="border-b last:border-0 hover:bg-accent transition-colors">
                        <td className="py-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-xs font-semibold text-primary">
                              {initials}
                            </div>
                            <div>
                              <div className="font-medium text-sm">{lead.contact_name}</div>
                              <div className="text-xs text-muted-foreground">{lead.business_name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(lead.status)}`}>
                            {lead.status || 'New'}
                          </span>
                        </td>
                        <td className="py-4 text-sm text-muted-foreground">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-4">
                          <div className="flex items-center space-x-2">
                            <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-xs font-semibold text-purple-600">
                              {ownerInitials}
                            </div>
                            <span className="text-sm text-muted-foreground">{lead.owner_name || 'Unassigned'}</span>
                          </div>
                        </td>
                        <td className="py-4 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/leads/${lead.id}`)}
                            data-testid={`view-lead-${lead.id}`}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Recent Activity */}
          <Card className="p-6" data-testid="dashboard-recent-activity">
            <h3 className="font-semibold text-lg mb-4">Recent Activity</h3>
            {recentActivity.length === 0 ? (
              <p className="text-sm text-muted-foreground">No activity recorded yet</p>
            ) : (
              <div className="space-y-4">
                {recentActivity.slice(0, 6).map((activity) => (
                  <div key={activity.id} className="flex space-x-3">
                    <div className="w-2 h-2 bg-primary rounded-full mt-1.5 flex-shrink-0"></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{activity.action}</div>
                      <div className="text-xs text-muted-foreground truncate">{activity.description}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{timeAgo(activity.timestamp)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Calendar Widget */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg">This Week</h3>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="grid grid-cols-7 gap-2">
            {WEEKDAY_LABELS.map((day) => (
              <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
                {day}
              </div>
            ))}
            {week.map((d, i) => {
              const isToday = d.toDateString() === todayStr;
              return (
                <div
                  key={i}
                  className={`aspect-square border rounded-lg flex flex-col items-center justify-center text-sm ${
                    isToday ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
                  }`}
                  data-testid={isToday ? 'dashboard-today-cell' : undefined}
                >
                  <div className="font-medium">{d.getDate()}</div>
                  {isToday && <div className="w-1 h-1 bg-white rounded-full mt-1"></div>}
                </div>
              );
            })}
          </div>
        </Card>
      </div>
  );
}

export default DashboardPage;
